import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'
import { leadKeys } from '../use-leads'
import { normalizeLead, type BackendLead } from '../normalize'

// Minimal lead context needed to call the AI enrich endpoint
export type LeadEnrichContext = {
  readonly linkedin_url?: string
  readonly full_name?: string
  readonly first_name?: string
  readonly last_name?: string
  readonly company?: string
  readonly job_title?: string
}

// ─── Enrichment result types ───────────────────────────────────────────────

export type CompanyInfo = {
  readonly size: string
  readonly industry: string
  readonly founded: string
  readonly hq_location: string
  readonly description: string
  readonly tech_stack: readonly string[]
}

export type NewsItem = {
  readonly title: string
  readonly summary: string
  readonly date: string
  readonly url: string
}

export type FundingInfo = {
  readonly stage: string
  readonly amount: string
  readonly investors: readonly string[]
}

export type EnrichmentResult = {
  readonly company_info: CompanyInfo
  readonly recent_news: readonly NewsItem[]
  readonly funding: FundingInfo
  readonly pain_points: readonly string[]
  readonly competitor_usage: readonly string[]
  readonly recommended_strategy: string
  readonly icp_fit_signals: readonly string[]
}

// ─── Message history types ─────────────────────────────────────────────────

export type MessageDirection = 'sent' | 'received'
export type MessageType = 'connection_request' | 'dm' | 'follow_up' | 'reply'

export type LeadMessage = {
  readonly id: string
  readonly direction: MessageDirection
  readonly message_type: MessageType
  readonly text: string
  readonly sent_at: string
}

// ─── Query keys ───────────────────────────────────────────────────────────

export const enrichmentKeys = {
  all: ['enrichment'] as const,
  detail: (leadId: string) => [...enrichmentKeys.all, leadId] as const,
}

export const messageKeys = {
  all: ['lead-messages'] as const,
  byLead: (leadId: string) => [...messageKeys.all, leadId] as const,
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/**
 * Fetch a single lead by ID. Uses the shared leadKeys so it reads from
 * the same cache that useLeads populates.
 */
export function useLeadById(id: string | null) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: leadKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await apiFetch<BackendLead>(`/api/leads/${id}`)
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to fetch lead ${id}`)
      return normalizeLead(res.data)
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

// Shape of res.data from POST /api/ai/enrich (after apiFetch envelope unwrap)
type AiEnrichResponse = {
  readonly enrichment: EnrichmentResult
}

/**
 * Mutation to run enrichment on a lead via POST /api/ai/enrich.
 * Accepts optional lead context (name, company, linkedin_url) to improve enrichment quality.
 * On success, stores the enrichment result in cache and invalidates the lead detail query.
 */
export function useRunEnrichment(leadId: string | null, context?: LeadEnrichContext) {
  const apiFetch = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<EnrichmentResult> => {
      if (!leadId) throw new Error('No lead selected')
      const name =
        context?.full_name ??
        [context?.first_name, context?.last_name].filter(Boolean).join(' ') ??
        ''
      const res = await apiFetch<AiEnrichResponse>('/api/ai/enrich', {
        method: 'POST',
        body: {
          lead_id: leadId,
          linkedin_url: context?.linkedin_url ?? '',
          name,
          company: context?.company ?? '',
          title: context?.job_title ?? '',
        },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to enrich lead')
      return res.data.enrichment
    },
    onSuccess: (result) => {
      if (!leadId) return
      // Store enrichment result in the local cache so panels render it without
      // a refetch.
      queryClient.setQueryData(enrichmentKeys.detail(leadId), result)
      // Refetch the lead row + the lists so the kanban/table show enriched_at,
      // company, intent_score, and any backfilled name fields immediately.
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) })
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: leadKeys.stats() })
    },
  })
}

// Backend message shape (from messages table)
type BackendMessage = {
  readonly id: string
  readonly direction: 'outbound' | 'inbound'
  readonly message_type: string
  readonly content: string
  readonly sent_at: string | null
  readonly created_at: string
}

function mapBackendMessage(msg: BackendMessage): LeadMessage {
  return {
    id: msg.id,
    direction: msg.direction === 'outbound' ? 'sent' : 'received',
    message_type: msg.message_type as LeadMessage['message_type'],
    text: msg.content,
    sent_at: msg.sent_at ?? msg.created_at,
  }
}

/**
 * Fetch message history for a lead.
 */
export function useLeadMessages(leadId: string | null) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: messageKeys.byLead(leadId ?? ''),
    queryFn: async () => {
      if (!leadId) return []
      const res = await apiFetch<readonly BackendMessage[]>(`/api/leads/${leadId}/messages`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to fetch lead messages')
      return res.data.map(mapBackendMessage)
    },
    enabled: Boolean(leadId),
    staleTime: 60_000,
  })
}

/**
 * Get cached enrichment result for a lead (populated after useRunEnrichment succeeds).
 */
export function useCachedEnrichment(leadId: string | null): EnrichmentResult | undefined {
  const queryClient = useQueryClient()
  if (!leadId) return undefined
  return queryClient.getQueryData<EnrichmentResult>(enrichmentKeys.detail(leadId))
}
